import type {
  AdapterSchemaAttribute,
  AdapterSchemaRelation,
} from '@/Atomic/Widgets/AiChatBot/core/engine/contracts'
import {
  getEntityRegistry,
  isEntityEnabled,
} from '@/Atomic/Widgets/AiChatBot/core/engine/entity-registry'
import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'
import type {
  CachedSchema,
  CachedSchemaAttribute,
  CachedSchemaRelation,
  EntityClassName,
  RelationSpec,
  SessionContext,
} from '@/Atomic/Widgets/AiChatBot/core/engine/types'

// Inlined from @/utils/CRUD/read/schema/getVisibleAttributes — pure data function,
// no Scrivito dependency. Kept here so the bot core has no @/utils imports.
// Exported so field-mapper reuses the single copy rather than importing @/utils.
export function getVisibleAttributes(
  schemaGroups: Record<string, { attributeOrder?: string[] } | undefined>,
  groupsOrder: string[] | undefined,
  schemaAttributes: Record<string, unknown>,
): { visibleAttributes: string[] } {
  const visibleAttributes: string[] = []
  if (!groupsOrder || !schemaGroups || !schemaAttributes) {
    return { visibleAttributes }
  }
  for (const groupName of groupsOrder) {
    const group = schemaGroups[groupName]
    if (!group?.attributeOrder) {
      continue
    }
    for (const attrName of group.attributeOrder) {
      if (schemaAttributes[attrName]) {
        visibleAttributes.push(attrName)
      }
    }
  }
  return { visibleAttributes }
}

/**
 * A readonly boolean is a computed/system flag that mirrors another field
 * (e.g. Opportunity `open` tracks `status`). Excluded from record serialization
 * so the model never cross-checks it against the field it duplicates. Shared by
 * buildSchema (cached path) and serializeDataItems (uncached fallback) so the
 * rule stays identical.
 */
export function isComputedFlag(attr: AdapterSchemaAttribute): boolean {
  return attr.readonly && attr.type === 'boolean'
}

export async function buildSchema(
  dataClass: EntityClassName,
  featureFlags: Set<string>,
): Promise<CachedSchema | null> {
  const { data } = getHostRuntime()
  const { schema, schemaGroups, groupsOrder, schemaAttributes } =
    await data.getSchema(data.getSchemaName(dataClass))

  if (!schemaAttributes || Object.keys(schemaAttributes).length === 0) {
    return null
  }

  const { visibleAttributes } = getVisibleAttributes(
    schemaGroups,
    groupsOrder,
    schemaAttributes,
  )

  const attributeKeys =
    visibleAttributes.length > 0
      ? visibleAttributes
      : Object.keys(schemaAttributes)

  const attributes = attributeKeys.flatMap((name) => {
    const attr = schemaAttributes[name]
    if (!attr) return []
    const entry: CachedSchemaAttribute = {
      name,
      title: attr.title,
      // warning: AdapterSchemaAttribute.type is string; CachedSchemaAttribute.type is the
      // AttributeType union. The adapter returns the same string values as the union members —
      // the widening is a type-contract artefact, not a real mismatch at runtime.
      type: attr.type as CachedSchemaAttribute['type'],
      mandatory: attr.mandatory,
      // Write-once fields (readonly + mandatory) must be set at creation, so they
      // are creatable but not updatable — mirrors the create drawer's
      // `readonly && !required` skip rule (src/utils/CRUD/read/dataClass/index.ts).
      creatable: attr.mandatory || !attr.readonly,
      updatable: !attr.readonly,
    }
    if (attr.type === 'enum' || attr.type === 'enumarray') {
      entry.values = (attr.enumValues ?? []).map((e) => ({
        value: e.value,
        label: e.title,
        // Co-dependency constraints (e.g. status option valid only for certain
        // group values). Carried through so get_write_schema can filter options.
        ...(e.constraints ? { constraints: e.constraints } : {}),
      }))
    }
    if (attr.type === 'reference' && attr.referenceTo) {
      entry.targetClass = attr.referenceTo
      // Carry referenceFilter so the concrete target class (e.g. Contact +
      // isExternal → Company) stays derivable for name→_id resolution.
      if (attr.referenceFilter) {
        // warning: AdapterReferenceFilter has optional booleans; engine/types CachedSchemaAttribute
        // expects the required-boolean ReferenceFilter from @/Data/typesV2. PISA values always
        // carry concrete booleans — the optionality is a contracts artefact. Single cast point.
        entry.referenceFilter =
          attr.referenceFilter as CachedSchemaAttribute['referenceFilter']
      }
    }
    return [entry]
  })

  // Filterable m:n / collection relations from the entity's own schema — the same
  // list the manual FilterBuilder reads (see useEntitySchema / buildAvailableEntities).
  const rawRelations = schema?.get('relations')
  const filterableRelations: CachedSchemaRelation[] = Array.isArray(
    rawRelations,
  )
    ? // schema.get returns unknown; narrowed to AdapterSchemaRelation[] after the
      // Array.isArray guard above (same pattern as build_filter.ts).
      (rawRelations as AdapterSchemaRelation[]).map((rel) => ({
        entityName: rel.entityName,
        relationPath: rel.relationPath,
        relationType: rel.relationType,
        title: data.getLocalizedTitle(
          rel,
          rel.entityName.charAt(0).toUpperCase() + rel.entityName.slice(1),
        ),
      }))
    : []

  const meta = getEntityRegistry()[dataClass]

  const relations = meta
    ? Object.fromEntries(
        Object.entries(meta.relations)
          // Hide relations whose target entity is disabled on this instance, so
          // the model is never told about a Tickets/Deal-pipeline relation it
          // cannot query. Mirrors the detail-page tab gate.
          .filter(([, rel]: [string, RelationSpec]) =>
            isEntityEnabled(rel.dataClass, featureFlags),
          )
          .map(([key, rel]: [string, RelationSpec]) => [
            key,
            {
              targetClass: rel.dataClass,
              filterKey: rel.filterKey,
              ...(rel.defaultOrder ? { defaultOrder: rel.defaultOrder } : {}),
            },
          ]),
      )
    : {}

  return {
    dataClass,
    attributes,
    allAttributeNames: Object.keys(schemaAttributes),
    serializableAttributeNames: Object.keys(schemaAttributes).filter((name) => {
      const attr = schemaAttributes[name]
      return !attr || !isComputedFlag(attr)
    }),
    relations,
    filterableRelations,
    defaultOrder: meta?.defaultOrder ?? [['updatedAt', 'desc']],
    userScopeFields: meta?.userScopeFields ?? [],
    ...(meta?.fetchHints ? { fetchHints: meta.fetchHints } : {}),
  }
}

export async function getOrBuildSchema(
  dataClass: EntityClassName,
  ctx: SessionContext,
): Promise<CachedSchema | null> {
  const cached = ctx.schemas.get(dataClass)
  if (cached) {
    return cached
  }
  const built = await buildSchema(dataClass, ctx.featureFlags)
  if (built) {
    ctx.schemas.set(dataClass, built)
  }
  return built
}
